import type { GoogleFlightBookingOption , SearchGoogleFlightsInput } from '@elara/domain/models/schemas/flight-schemas';

import type { AppFlowsContainer } from '#flows.js';
import type { FlightOptionModel } from '#models/FlightOptionModel.js';

import { getErrorReporter } from '#lib/error-reporting.js';
import { createState } from '#lib/signals-utils.js';

/** Optional overrides for currency and locale when loading booking options. */
export interface FlightBookingOptionsOverrides {
  currency?: string;
  countryCode?: string;
}

/**
 * Builds GetFlightBookingOptions flow input from a flight option model.
 * Returns null if the option doesn't have enough data (e.g. missing bookingToken or segments).
 */
export function buildGetFlightBookingOptionsInput(
  flightOption: FlightOptionModel,
  overrides?: FlightBookingOptionsOverrides,
): (SearchGoogleFlightsInput & { bookingToken: string } & FlightBookingOptionsOverrides) | null {
  const bookingToken = flightOption.bookingToken;
  if (!bookingToken) return null;

  const firstOutbound = flightOption.firstFlight('outbound');
  const lastOutbound = flightOption.lastFlight('outbound');
  const firstReturn = flightOption.firstFlight('return');
  if (!firstOutbound || !lastOutbound) return null;

  const originCode = firstOutbound.originCode;
  const destinationCode = lastOutbound.destinationCode;
  if (!originCode || !destinationCode) return null;

  const departureDate = firstOutbound.departure?.slice(0, 10);
  if (!departureDate) return null;

  const passengers = flightOption.passengers;

  return {
    origin: [originCode],
    destination: [destinationCode],
    departureDate,
    returnDate: flightOption.roundTrip && firstReturn
      ? firstReturn.departure?.slice(0, 10)
      : undefined,
    adults: passengers.adults ?? 1,
    children: passengers.children ?? 0,
    infants: passengers.infants ?? 0,
    cabinClass: 'Economy',
    searchType: flightOption.roundTrip ? 'round-trip' : 'one-way',
    bookingToken,
    ...(overrides && {
      ...(overrides.currency !== undefined && { currency: overrides.currency }),
      ...(overrides.countryCode !== undefined && { countryCode: overrides.countryCode }),
    }),
  };
}

export function createFlightBookingOptionsState(flows: AppFlowsContainer) {
  return createState({
    options: undefined as GoogleFlightBookingOption[] | undefined,
    isLoading: false,
    error: undefined as string | undefined,

    reset() {
      this.options = undefined;
      this.error = undefined;
    },

    async loadBookingOptions(flightOption: FlightOptionModel, overrides?: FlightBookingOptionsOverrides) {
      const input = buildGetFlightBookingOptionsInput(flightOption, overrides);

      if (!input) {
        this.error = 'Missing flight details needed to load booking options';
        this.options = undefined;
        return;
      }

      try {
        this.isLoading = true;
        this.error = undefined;
        this.options = undefined;

        const result = await flows.getFlightBookingOptions(input);
        this.options = result.data.results;
        this.error = undefined;
      } catch (err) {
        getErrorReporter().captureException(err);
        this.error = err instanceof Error ? err.message : 'Failed to load booking options';
        this.options = undefined;
      } finally {
        this.isLoading = false;
      }
    },
  });
}

export type FlightBookingOptionsState = ReturnType<typeof createFlightBookingOptionsState>;
